import { MainContentContainer } from "@app/components/MainContainer";
import { MainContent } from "@app/components/MainContainer/MainContent";
import { MainDescription } from "@app/components/MainContainer/MainDescription";
import { MainHeader } from "@app/components/MainContainer/MainHeader";
import { MainTitle } from "@app/components/MainContainer/MainTitle";
import { CommonErrorModal } from "@app/components/modals/CommonErrorModal";
import { CommonModal } from "@app/components/modals/CommonModal";
import { Cafe24LoginModal } from "@app/components/modals/PlatformModal/Cafe24";
import { CAFE24_SCOPES } from "@app/pages/auth/platform/cafe24/Login";
import { Path } from "@app/route_path";
import { CAFE24_CLIENT_ID } from "@app/utils/keys";
import { Button, Input, useModal } from "@shortsworks/ui";
import {
  getCafe24ThemeList,
  useDeleteCafe24Script,
  useGetAccount,
  useGetAccountAccessKey,
  useGetAccountPlatform,
  useGetCafe24ScriptList,
  useSetCafe24Script,
  useUpdateCafe24Script,
} from "@shortsworks/user-api";
import * as React from "react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { LoaderCircle } from "lucide-react";
import { PlatformType } from "@app/types/platform";
import SyntaxHighlighter from "react-syntax-highlighter";
import { docco } from "react-syntax-highlighter/dist/esm/styles/hljs";

export const SDK_PATH = import.meta.env.VITE_FONTEND_URL + `/sdk/index.js`;

// 다른 방법으로 표시 가능한지 체크
const scriptExample = `
<!DOCTYPE html>
<html>
<head>
    <!-- 해당 위치에 쇼츠웍스 스크립트를 삽입해주세요.   -->
    
</head>
<body>
</body>
</html>
`;

export const Integration = () => {
  const { showModal } = useModal();

  const { data: accountPlatformData, isSuccess: isSuccessGetAccountPlatform } =
    useGetAccountPlatform();
  const { data: accountData, isSuccess: isSuccessGetAccount } = useGetAccount();

  // data가 확인되지 않은 경우 발생하는 에러로 인한 처리 필요
  if (!isSuccessGetAccountPlatform || !isSuccessGetAccount) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <LoaderCircle className="w-6 h-6 mr-2 animate-spin" />
      </div>
    );
  }

  const { platformType, platformId } = accountPlatformData.data;
  const copyScript = () => {
    navigator.clipboard.writeText(SDK_PATH);
    return showModal(CommonModal, {
      title: "URL이 정상적으로 복사되었습니다.",
    });
  };

  return (
    <MainContentContainer>
      <MainHeader>
        <MainTitle>연동하기</MainTitle>
        <MainDescription>
          쇼핑몰에 쇼츠웍스 위젯을 연동하는 방법에 대한 내용입니다.
        </MainDescription>
      </MainHeader>
      <MainContent>
        <div className="max-w-[90vw]">
          {/* 모바일 사이즈에서 벗어나는 점 수정 tailwind 값을 등록 및 원인 필요 */}
          {platformType === PlatformType.CAFE24 && <Cafe24Intergration platformId={platformId} />}
          {platformType === PlatformType.BLOGPAY && <BlogpayIntegration />}
          {/* 컴포넌트로 분리하기*/}
          {platformType !== PlatformType.BLOGPAY && (
            // 플랫폼 없는 경우 상단 여백 발생
            <div className="mt-8">
              <div className="font-bold">사이트 HTML에 설치하기</div>
              <div>
                사이트의 &lt;head&gt; 태그와 &lt;/head&gt; 사이에 붙여넣어야
                합니다.
              </div>
              <div className="mt-2 flex flex-row gap-2 pr-2">
                <Input defaultValue={SDK_PATH} />
                <Button onClick={copyScript}>복사하기</Button>
              </div>
            </div>
          )}
          <div className="mt-4 text-sm md:text-md">
            <SyntaxHighlighter language="html" style={docco}>
              {scriptExample}
            </SyntaxHighlighter>
          </div>
        </div>

      </MainContent>
    </MainContentContainer>
  );
};

const Cafe24Intergration = ({ platformId }: { platformId: string }) => {
  const { showModal } = useModal();
  const [isScriptInstalled, setIsScriptInstalled] = useState<
    boolean | "loading"
  >("loading");

  const queryClient = useQueryClient();
  // 해당 기능은 회원 가입 플로우 중에서도 이용됨
  const {
    data: cafe24ScriptData,
    queryKey: cafe24ScriptListQueryKey,
    isSuccess: isSuccessGetCafe24ScriptList,
    isError: isErrorGetCafe24ScriptList,
  } = useGetCafe24ScriptList(platformId);
  const {
    mutateAsync: mutateAsyncSetCafe24Script,
    isPending: isPendingSetCafe24Script,
  } = useSetCafe24Script({
    mutation: {
      onSuccess: () => {
        return queryClient.invalidateQueries({
          queryKey: cafe24ScriptListQueryKey,
        });
      },
    },
  });
  const { mutateAsync: mutateAsyncUpdateCafe24Script } = useUpdateCafe24Script({
    mutation: {
      onSuccess: () => {
        return queryClient.invalidateQueries({
          queryKey: cafe24ScriptListQueryKey,
        });
      },
    },
  });
  const { mutateAsync: mutateAsyncDeleteCafe24Script } = useDeleteCafe24Script({
    mutation: {
      onSuccess: () => {
        return queryClient.invalidateQueries({
          queryKey: cafe24ScriptListQueryKey,
        });
      },
    },
  });

  const getExistScript = async () => {
    const [pc, mobile] = await Promise.all([
      getCafe24ThemeList(platformId, { deviceType: "pc" }),
      getCafe24ThemeList(platformId, { deviceType: "mobile" }),
    ]);

    //@ts-ignore
    const themes = [...pc.themes, ...mobile.themes];
    const numbers = themes.map((theme) => theme["skin_no"]);

    return {
      // @ts-ignore
      script: cafe24ScriptData.scripttags.find((script) => script.src === SDK_PATH,
      ),
      numbers,
    };
  };

  useEffect(() => {
    if (!isSuccessGetCafe24ScriptList) {
      return;
    }
    getExistScript().then(({ script }) => {
      if (typeof script !== "undefined") {
        setIsScriptInstalled(true);
      } else {
        setIsScriptInstalled(false);
      }
    });
  }, [cafe24ScriptData]);

  const installCafe24Script = async () => {
    if (!isSuccessGetCafe24ScriptList) {
      return showModal(CommonErrorModal, {
        title: "스크립트를 불러오기 실패했습니다.",
      });
    }

    try {
      const { script, numbers } = await getExistScript();

      let result;
      if (!script) {
        result = await mutateAsyncSetCafe24Script({
          data: {
            mall_id: platformId,
            src: SDK_PATH,
            skin_no: numbers,
            integrity: "",
          },
        });
      } else {
        result = await mutateAsyncUpdateCafe24Script({
          data: {
            mall_id: platformId,
            src: SDK_PATH,
            //@ts-ignore
            script_no: numbers,
            integrity: "",
          },
        });
      }

      // @ts-ignore
      if (!result?.scripttag) {
        return showModal(CommonErrorModal, {
          title: "스크립트 설치를 실패했습니다.",
        });
      }
      return showModal(CommonModal, { title: "스크립트가 설치되었습니다." });
    } catch (error) {
      showModal(CommonErrorModal, { title: "스크립트 설치를 실패했습니다." });
    }
  };

  const removeCafe24Script = async () => {
    if (!isSuccessGetCafe24ScriptList) {
      return showModal(CommonErrorModal, {
        title: "스크립트를 불러오기 실패했습니다.",
      });
    }
    // @ts-ignore
    const script = cafe24ScriptData.scripttags.find((script) => script.src === SDK_PATH,
    );
    if (typeof script === "undefined") {
      return showModal(CommonErrorModal, {
        title: "스크립트가 설치되어있지 않습니다.",
      });
    }
    const scriptNo = script["script_no"];
    const result = await mutateAsyncDeleteCafe24Script({
      data: { mall_id: platformId, script_no: scriptNo },
    });
    // @ts-ignore
    if (typeof result?.scripttag === "undefined") {
      return showModal(CommonErrorModal, {
        title: "스크립트 삭체를 실패했습니다.",
      });
    }

    return showModal(CommonModal, { title: "스크립트가 삭제되었습니다." });
  };

  const handleClickLoginCafe24 = () => {
    // 공통 로직 정리 필요
    const redirectState = btoa(
      `${platformId}@${import.meta.env.VITE_FONTEND_URL}${Path.LOGIN_CAFE24}?mall_id=${platformId}`,
    );
    const cafe24AuthRedirectURL = `https://${platformId}.cafe24.com/api/v2/oauth/authorize?response_type=code&client_id=${CAFE24_CLIENT_ID}&redirect_uri=${"https://api.shortsdev.com"}/external/auth/&state=${redirectState}&scope=${CAFE24_SCOPES}`;
    return showModal(Cafe24LoginModal, { redirectUrl: cafe24AuthRedirectURL });
  };

  return (
    <div>
      <div className="font-bold">스크립트 설치</div>
      <div>
        설치 버튼을 클릭하면 스크립트가 자동으로 삽입되어 위젯을 적용할 준비를
        마치게 됩니다.
      </div>
      {/* 로딩이 지속될 경우, cafe24 관련 권한 문제인지 체크 필요 */}
      {isErrorGetCafe24ScriptList && (
        <span className="text-red-500 text-sm">
          카페24 토큰이 만료되었습니다.{" "}
          <span
            onClick={handleClickLoginCafe24}
            className="underline cursor-pointer"
          >
            카페24 로그인
          </span>
        </span>
      )}
      {/* loading 상태 체크 필요 */}
      {isSuccessGetCafe24ScriptList && (
        <>
          <Button
            className="w-32 mt-4 mr-2"
            type="button"
            onClick={installCafe24Script}
            disabled={isScriptInstalled === "loading" || isScriptInstalled}
          >
            {isScriptInstalled === "loading" ? (
              <LoaderCircle className="w-6 h-6 mr-2 animate-spin" />
            ) : (
              "스크립트 설치"
            )}
          </Button>
          <Button
            className="w-32 mt-4 mr-2"
            type="button"
            variant="secondary"
            onClick={removeCafe24Script}
            disabled={isScriptInstalled === "loading" || !isScriptInstalled}
          >
            {isScriptInstalled === "loading" ? (
              <LoaderCircle className="w-6 h-6 mr-2 animate-spin" />
            ) : (
              "스크립트 제거"
            )}
          </Button>
        </>
      )}
    </div>
  );
};

const BlogpayIntegration = () => {
  const { showModal } = useModal();
  const {
    data: accountAccessKeyData,
    isSuccess: isSuccessGetAccountAccessKey,
  } = useGetAccountAccessKey();
  const { mutate: mutateCopyScript, isPending: isPendingCopyScript } =
    useMutation({
      mutationFn: async (text: string) => {
        if (!navigator.clipboard) {
          const textarea = document.createElement("textarea");
          textarea.style.position = "absolute";
          textarea.style.zIndex = "-1";
          textarea.value = text;
          document.body.appendChild(textarea);
          textarea.select();
          document.execCommand("copy");
          return document.body.removeChild(textarea);
        }
        return navigator.clipboard.writeText(text);
      },
      onSuccess: () => {
        return showModal(CommonModal, { title: "엑세스 키가 복사되었습니다." });
      },
      onError: () => {
        return showModal(CommonErrorModal, {
          title: "엑세스 키가 복사를 실패했습니다.",
        });
      },
    });

  const handleClickCopyScript = async () => {
    if (!isSuccessGetAccountAccessKey) {
      return showModal(CommonErrorModal, {
        title: "엑세스 키를 불러오는 데 실패했습니다.",
      });
    }
    return mutateCopyScript(accountAccessKeyData.data.accessKey);
  };

  if (!isSuccessGetAccountAccessKey) {
    return (
      <div className="flex w-full h-full items-center justify-center">
        <LoaderCircle className="w-6 h-6 mr-2 animate-spin" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-row gap-2">
        <div className="font-bold mb-2">블로그페이에 쇼츠웍스 연동하기</div>
        <Link
          className="text-primary underline"
          to="https://imgibble.notion.site/11b21e3004b48159a38fe07ef5fe10cc"
          target="_blank"
        >
          [설치 가이드]
        </Link>
      </div>

      <div className="mt-2">
        다음의 코드를 복사해서 블로그페이 쇼츠웍스 연동 관리 메뉴에
        추가해주세요.
      </div>
      <div className="mt-2 flex flex-row gap-4">
        <Input defaultValue={accountAccessKeyData.data.accessKey} />
        <Button disabled={isPendingCopyScript} onClick={handleClickCopyScript}>
          복사하기
        </Button>
      </div>
    </div>
  );
};
